import { IntegrationSystem, OrderChannel } from '@biteinc/enums';
import { UniversalPaymentHelper } from '@biteinc/helpers-universal';

namespace StoredValueHelper {
  export const freedomPayStoredValueCardSchemeIdStorageKey = 'freedomPayStoredValueCardSchemeId';

  const storedValueLikeCampusCardSystems = [
    IntegrationSystem.CbordCampusCard,
    IntegrationSystem.QuickchargeCampusCard,
  ] as const satisfies readonly IntegrationSystem.CampusCard[];

  export type StoredValueLikeCampusCardSystem = (typeof storedValueLikeCampusCardSystems)[number];
  export type StoredValueLikeSystem =
    | IntegrationSystem.StoredValue
    | StoredValueLikeCampusCardSystem;

  export function isStoredValueLikeCampusCardSystem(
    campusCardSystem: IntegrationSystem.CampusCard | undefined,
  ): campusCardSystem is StoredValueLikeCampusCardSystem {
    return (
      !!campusCardSystem &&
      storedValueLikeCampusCardSystems.includes(campusCardSystem as StoredValueLikeCampusCardSystem)
    );
  }

  // Returns true if the gift card is actually some sort of secondary payment method, like a
  // student card or some other stored payment method.
  export function isStoredValueSecondaryPayment(): boolean {
    return !!gcn.location.getStoredValueIntegration()?.useAsSecondaryPayment;
  }

  export function requiresNativeForStoredValue(
    storedValueSystem: StoredValueLikeSystem | undefined,
  ): boolean {
    if (!storedValueSystem) {
      return false;
    }

    switch (storedValueSystem) {
      case IntegrationSystem.Archer:
      case IntegrationSystem.AurusPayStoredValue:
      case IntegrationSystem.ChaseStoredValue:
      case IntegrationSystem.EigenStoredValue:
      case IntegrationSystem.FreedomPayKioskStoredValue:
      case IntegrationSystem.ParPayStoredValue:
      case IntegrationSystem.PinataPayNativeStoredValue:
      case IntegrationSystem.WorldpayStoredValue:
        return true;
      case IntegrationSystem.AtriumStoredValue:
        // On the kiosk a physical card needs to be swiped because students might know each other's
        // student IDs (and hence Atrium card numbers)
        if (
          [OrderChannel.DriveThru, OrderChannel.Kiosk].includes(gcn.location.get('orderChannel'))
        ) {
          return true;
        }
        // On a linebuster, a staff member is entering the number so we will only use a native
        // reader if we have one connected to the kiosk.
        return !!gcn.kiosk?.get('hasMsr');
      case IntegrationSystem.FreedomPayStoredValue:
      case IntegrationSystem.Givex:
      case IntegrationSystem.OloStoredValue:
      case IntegrationSystem.PaytronixGift:
      case IntegrationSystem.PinataPayStoredValue:
      case IntegrationSystem.PinataStoredValue:
      case IntegrationSystem.StarbucksStoredValue:
      case IntegrationSystem.Tcc:
      case IntegrationSystem.Valutec:
      case IntegrationSystem.CbordCampusCard:
      case IntegrationSystem.CbordStoredValue:
      case IntegrationSystem.QuickchargeCampusCard:
        if (gcn.kiosk?.get('hasHid') || gcn.kiosk?.get('hasMsr')) {
          return true;
        }
        return false;
    }
  }

  export function getStoredValueSsoUrl(
    storedValueSystem: StoredValueLikeSystem,
    authUrl: string,
    redirectUrl: string,
  ): string {
    switch (storedValueSystem) {
      case IntegrationSystem.AtriumStoredValue:
        return `${authUrl}&partner=bite&url=${redirectUrl}`;
      case IntegrationSystem.CbordCampusCard:
      case IntegrationSystem.CbordStoredValue:
        return `${authUrl}&redirecturl=${redirectUrl}`;
      case IntegrationSystem.FreedomPayStoredValue: {
        const url = new URL(authUrl);
        url.searchParams.set('redirecturl', redirectUrl);
        return url.toString();
      }
      case IntegrationSystem.Archer:
      case IntegrationSystem.AurusPayStoredValue:
      case IntegrationSystem.ChaseStoredValue:
      case IntegrationSystem.EigenStoredValue:
      case IntegrationSystem.FreedomPayKioskStoredValue:
      case IntegrationSystem.Givex:
      case IntegrationSystem.OloStoredValue:
      case IntegrationSystem.ParPayStoredValue:
      case IntegrationSystem.PaytronixGift:
      case IntegrationSystem.PinataPayNativeStoredValue:
      case IntegrationSystem.PinataPayStoredValue:
      case IntegrationSystem.PinataStoredValue:
      case IntegrationSystem.QuickchargeCampusCard:
      case IntegrationSystem.StarbucksStoredValue:
      case IntegrationSystem.Tcc:
      case IntegrationSystem.Valutec:
      case IntegrationSystem.WorldpayStoredValue:
        throw new Error(`${storedValueSystem} does not support SSO authentication`);
    }
  }

  export function getMultipleAccountStoredValueSsoAuthUrl(
    storedValueSystem: StoredValueLikeSystem,
    authUrl: string,
  ): string {
    switch (storedValueSystem) {
      case IntegrationSystem.FreedomPayStoredValue:
        return getFreedomPayMultipleAccountSsoAuthUrl(authUrl);
      case IntegrationSystem.CbordCampusCard:
      case IntegrationSystem.QuickchargeCampusCard:
      case IntegrationSystem.AtriumStoredValue:
      case IntegrationSystem.CbordStoredValue:
      case IntegrationSystem.Archer:
      case IntegrationSystem.AurusPayStoredValue:
      case IntegrationSystem.ChaseStoredValue:
      case IntegrationSystem.EigenStoredValue:
      case IntegrationSystem.FreedomPayKioskStoredValue:
      case IntegrationSystem.Givex:
      case IntegrationSystem.OloStoredValue:
      case IntegrationSystem.ParPayStoredValue:
      case IntegrationSystem.PaytronixGift:
      case IntegrationSystem.PinataPayNativeStoredValue:
      case IntegrationSystem.PinataPayStoredValue:
      case IntegrationSystem.PinataStoredValue:
      case IntegrationSystem.StarbucksStoredValue:
      case IntegrationSystem.Tcc:
      case IntegrationSystem.Valutec:
      case IntegrationSystem.WorldpayStoredValue:
        return authUrl;
    }
  }

  function getFreedomPayMultipleAccountSsoAuthUrl(authUrl: string): string {
    try {
      const url = new URL(authUrl);
      const pathParts = url.pathname.split('/');
      const authPage = pathParts[pathParts.length - 1];
      if (!authPage || authPage.startsWith('mlogin') || !authPage.startsWith('login')) {
        return authUrl;
      }
      pathParts[pathParts.length - 1] = `m${authPage}`;
      url.pathname = pathParts.join('/');
      return url.toString();
    } catch {
      return authUrl;
    }
  }

  export function storedValueSupportsSavedCards(storedValueSystem: StoredValueLikeSystem): boolean {
    switch (storedValueSystem) {
      case IntegrationSystem.AtriumStoredValue:
      case IntegrationSystem.CbordCampusCard:
      case IntegrationSystem.CbordStoredValue:
      case IntegrationSystem.FreedomPayStoredValue:
        return true;
      case IntegrationSystem.Archer:
      case IntegrationSystem.AurusPayStoredValue:
      case IntegrationSystem.ChaseStoredValue:
      case IntegrationSystem.EigenStoredValue:
      case IntegrationSystem.FreedomPayKioskStoredValue:
      case IntegrationSystem.Givex:
      case IntegrationSystem.OloStoredValue:
      case IntegrationSystem.ParPayStoredValue:
      case IntegrationSystem.PaytronixGift:
      case IntegrationSystem.PinataPayNativeStoredValue:
      case IntegrationSystem.PinataPayStoredValue:
      case IntegrationSystem.PinataStoredValue:
      case IntegrationSystem.QuickchargeCampusCard:
      case IntegrationSystem.StarbucksStoredValue:
      case IntegrationSystem.Tcc:
      case IntegrationSystem.Valutec:
      case IntegrationSystem.WorldpayStoredValue:
        return false;
    }
  }

  // Returns false when the location's CBORD stored-value or campus-card integration permits a
  // zero-balance charge/inclining-balance account to be spent (charged like credit). A location
  // configures CBORD as either a stored-value or campus-card integration, so we check both.
  export function balanceLimitsStoredValueOrCampusCardSpend(
    storedValueSystem: IntegrationSystem.StoredValue | undefined,
    campusCardSystem: IntegrationSystem.CampusCard | undefined,
    accountType: number | string | undefined,
  ): boolean {
    return (
      UniversalPaymentHelper.balanceLimitsStoredValueSpend(storedValueSystem, accountType) &&
      UniversalPaymentHelper.balanceLimitsCampusCardSpend(campusCardSystem, accountType)
    );
  }
}

export default StoredValueHelper;
